import { XMarkIcon } from '@heroicons/react/24/outline';
import { Form, Link, useFetcher, useLocation, useNavigation } from '@remix-run/react';
import { GQLStaffRoleRoleType } from '@theprintbar/graphql';
import type { UserSessionData } from '@theprintbar/keystone';
import { UserPrefs } from '@theprintbar/server';
import { WebRoutes, cn } from '@theprintbar/shared';
import { AccountIcon, BrandIcon, BrandLogo, ButtonSize, Container, TransparentButton } from '@theprintbar/ui';
import { createElement, useCallback, useEffect, useRef, useState } from 'react';
import { RootLoaderData } from '~/root';
import { Marquee } from '../Marquee';
import { Cart } from './components/Cart';
import { CategoryDropdown } from './components/CategoryDropdown';
import { MegaMenu } from './components/MegaMenu';
import { QuoteBar } from './components/QuoteBar';
import { SearchMenu } from './components/SearchMenu';

interface HeaderProps {
	data: RootLoaderData;
}

export function Header({ data }: HeaderProps) {
	const { globalContent, adminDomain, crewDomain, categories } = data;
	const [quote, setQuote] = useState<string>('');
	const { pathname } = useLocation();
	const navRef = useRef<HTMLDivElement>(null);
	const [activeMenu, setActiveMenu] = useState<string | undefined>(undefined);
	const navigation = useNavigation();

	const sessionFetcher = useFetcher<{ success: boolean; session: UserSessionData; userPrefs: UserPrefs }>();

	const changeMenu = useCallback(
		(menu: string, active: boolean) => {
			if (menu === activeMenu && !active) {
				setActiveMenu(undefined);
			} else if (menu !== activeMenu && active) {
				setActiveMenu(menu);
			}
		},
		[activeMenu],
	);

	useEffect(() => {
		sessionFetcher.load(WebRoutes.API.auth.fetchSession);
	}, []);

	//On navigation close menu
	useEffect(() => {
		if (navigation.state !== 'idle' && activeMenu) {
			setActiveMenu(undefined);
		}
	}, [navigation.state]);

	const { topBanner, topLink, mainLinks } = globalContent;

	const topLinkProps = topLink ? { href: topLink || undefined, target: '_blank' } : {};

	const topCategories = categories?.filter(c => !c.parents?.length).sort((a, b) => a.order - b.order);

	const hiddenRoutes = [WebRoutes.Checkout.Information, WebRoutes.PaymentRequest.Base];
	return !!hiddenRoutes.find(r => pathname.includes(r)) ? null : (
		<header className="bg-white-pearl sticky top-0 z-50">
			<QuoteBar quote={quote} />
			{/* Top Banner */}
			<div className="relative bg-white-mid hidden lg:flex items-center h-12">
				<Container className="flex justify-end text-xs font-normal py-4 md:py-0">
					{topBanner &&
						createElement('a', {
							...topLinkProps,
						})}
					<div className="flex flex-1 items-center space-x-4 justify-center h-full absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 ">
						{mainLinks.structure?.map(link => (
							<Link
								key={`${link?.to}-${link?.text}`}
								to={link?.to || '#'}
								className="px-2 h-full text-xs relative flex items-center justify-center after:content-[''] after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-current after:scale-x-0 after:hover:scale-x-100 after:transition-transform after:duration-700 after:ease-in-out"
							>
								<span className="relative">{link?.text}</span>
							</Link>
						))}
					</div>
					<div
						className={cn(
							'items-center py-1 space-x-5 flex transition-opacity duration-500 ease-in-out',
							!!sessionFetcher.data?.success ? 'opacity-100' : 'opacity-0',
						)}
					>
						{!!sessionFetcher.data?.success ? (
							sessionFetcher.data.session ? (
								<>
									{!!sessionFetcher.data.session.staffRole && (
										<>
											{sessionFetcher.data.session.staffRole.role !== GQLStaffRoleRoleType.Staff && (
												<a target="_blank" href={adminDomain}>
													<h6 className="text-xs">Admin Portal</h6>
												</a>
											)}
											<a target="_blank" href={crewDomain}>
												<h6 className="text-xs">Crew Portal</h6>
											</a>
										</>
									)}
									{/* TODO: Add links to POD for POD users or Back end for staff */}
									<Link prefetch="intent" to={WebRoutes.Account.Landing}>
										<h6 className="text-xs">My Account</h6>
									</Link>
									<form method="post" action={WebRoutes.Logout}>
										<TransparentButton size={ButtonSize.S} type="submit" className="px-0">
											<h6 className="text-xs">Logout</h6>
										</TransparentButton>
									</form>
								</>
							) : (
								<>
									<Link prefetch="intent" to={`${WebRoutes.Login}${pathname === '/' ? '' : `?returnTo=${pathname}`}`}>
										<h6 className="text-xs">Sign In</h6>
									</Link>
									<Link prefetch="intent" to={WebRoutes.CreateAccount.Landing}>
										<h6 className="text-xs">Create an Account</h6>
									</Link>
								</>
							)
						) : null}
					</div>
				</Container>
			</div>
			{/* Main Menu Bar */}
			<nav
				ref={navRef}
				className={cn(
					'flex lg:border-t border-blue',
					!!globalContent.marqueeText && !sessionFetcher?.data?.userPrefs?.hideBanner ? '' : 'border-b',
				)}
			>
				<Container className="relative flex items-center justify-between px-0">
					{/* Logo */}
					<div className="hidden lg:flex flex-1">
						<Link to={WebRoutes.Home} className="flex items-center h-14">
							<span className="sr-only">The Print Bar Logo</span>
							<BrandLogo className="fill-blue hidden xs:block h-5 w-auto lg:h-6" />
							<BrandIcon className="fill-blue h-8 w-auto xs:hidden" />
						</Link>
					</div>
					<MegaMenu
						className="h-full"
						data={data}
						categories={categories || []}
						navRef={navRef}
						active={activeMenu === 'mega'}
						setActive={(active: boolean) => changeMenu('mega', active)}
					/>
					<div className="flex lg:hidden flex-1">
						<Link to={WebRoutes.Home} className="flex items-center h-14">
							<span className="sr-only">The Print Bar Logo</span>
							<BrandLogo className="fill-blue hidden xs:block h-5 w-auto lg:h-6" />
							<BrandIcon className="fill-blue h-8 w-auto xs:hidden" />
						</Link>
					</div>
					<div className="hidden lg:flex flex-shrink-0 items-center justify-center text-base font-normal h-full">
						{topCategories?.map(category => (
							<CategoryDropdown
								activeCategory={activeMenu}
								setActiveCategory={() => changeMenu(category.code, true)}
								navRef={navRef}
								key={category.slug}
								category={category}
								categories={categories?.sort((a, b) => a.order - b.order) || []}
							/>
						))}
					</div>
					{/* Others in Menu */}
					<div className="flex flex-1 items-center justify-end lg:space-x-3 h-full">
						<div className="h-full flex items-center">
							<SearchMenu data={data} />
						</div>
						<div className="lg:hidden h-full">
							<Link to={WebRoutes.Account.Landing} className="flex h-full px-4 items-center">
								<AccountIcon className="w-5 h-5" />
							</Link>
						</div>
						<div className="h-full flex items-center">
							<Cart setQuote={setQuote} session={sessionFetcher.data?.session} />
						</div>
					</div>
				</Container>
			</nav>
			{!sessionFetcher?.data?.userPrefs?.hideBanner && !!globalContent.marqueeText && (
				<div className="relative">
					<Marquee content={globalContent} />
					<Form method="post" action="" className="absolute inset-y-0 right-2 flex items-center">
						<button className="p-1 rounded-full border border-blue bg-pink" type="submit" name="hideBanner" value="true">
							<XMarkIcon className="w-5 h-5" />
						</button>
					</Form>
				</div>
			)}
		</header>
	);
}
